/**
 * Xhosa catalog (Latin) for the Phase 2 `MessageId` set — Agent 9, batch 3.
 *
 * `dir: 'ltr'`. Native-grade isiXhosa at a mathematician's register. **Warrant → «Isiqinisekiso»**
 * (justification/warrant — flagged). Scientific loanwords are used where isiXhosa standardly
 * borrows (logic → Ilojiki, modal → Imodali, heuristic, scalar, geometry). W/M grades stay
 * Latin (`::numbering-system/latn`). CLDR cardinal categories are one / other; the noun uses
 * the isi-/izi- class (isithintelo → izithintelo). `frameActive` declares one/other plus an
 * explicit `=0`. Per INV-STABLE-ID a wrong translation is a presentation concern only — every
 * verdict stays fixed.
 */
import { P2 } from '../message-ids-phase2.js';
export const XH_CATALOG = {
    locale: 'xh',
    dir: 'ltr',
    messages: {
        [P2.inquiryTitle]: 'Uphando',
        [P2.sectionClaims]: 'Amabango',
        [P2.sectionIntegrity]: 'Ingqibelelo',
        [P2.axisWarrantLabel]: 'Isiqinisekiso',
        [P2.axisWarrantValue]: 'W{w, number, ::numbering-system/latn}',
        [P2.axisEmpiricalLabel]: 'Okovavanyo',
        [P2.axisEmpiricalValue]: '{kind, select, defn {Inkcazelo} interp {Utoliko} testable {Okuvavanyekayo} tested {Okuvavanyiweyo} refuted {Okuphikisiweyo} program {Inkqubo yophando} philos {Okwefilosofi} other {Akwaziwa}}',
        [P2.axisModalLabel]: 'Imodali',
        [P2.axisModalValue]: 'M{m, number, ::numbering-system/latn}',
        [P2.axisKidSummary]: 'Inqanaba lamandla {w, number, ::numbering-system/latn}',
        [P2.stateExplorable]: 'Okuphandekayo',
        [P2.stateRefuted]: 'Okuphikisiweyo',
        [P2.stateOutside]: 'Ngaphandle komda',
        [P2.verdictConflict]: 'Iyangqubana ne-{constraints}',
        [P2.verdictOutside]: 'Ifuna i-{missing}',
        [P2.lossRegisterPlain]: 'Yenziwe lula kulwimi oluqhelekileyo — enye ingqibelelo ishiyiwe.',
        [P2.lossRegisterKid]: 'Yenziwe lula kubafundi abancinci — iinkcukacha ezininzi zishiyiwe.',
        [P2.lossProfileDetail]: 'Iprofayile epheleleyo ye-Isiqinisekiso / Okovavanyo / Imodali ishwankathelwe apha.',
        [P2.lossConflictDetail]: 'Iinkcukacha zongquzulwano zishunqulelwe kolu mboniso.',
        [P2.laneVerified]: 'Kuqinisekisiwe',
        [P2.laneUnverified]: 'Akuqinisekiswanga',
        [P2.laneConflict]: 'Ungquzulwano luqinisekisiwe',
        [P2.constraintLogic]: 'Ilojiki',
        [P2.constraintLightBends]: 'Ukukhanya kuyagoba kwintsimi yomtsalane',
        [P2.constraintParallelPostulate]: 'I-postulate yemigca efanayo',
        [P2.claimScalarGravity]: 'Ithiyori ye-scalar yomtsalane',
        [P2.claimNonEuclidean]: 'Ijiyometri engeyiyo eka-Euclid',
        [P2.claimSubstrateSr]: 'Utoliko lwe-substrate lobudlelwane obukhethekileyo',
        [P2.edgeExtObservation]: 'Ukuqwalasela kwangaphandle',
        [P2.edgeResourceSample]: 'Uvimba wekhompyutha owabelwanayo',
        [P2.edgeHeuristicSample]: 'Isikhokelo se-heuristic',
        [P2.edgeSharedSample]: 'Isiseko esabelwanayo',
        // --- Phase 2 view-chrome ---
        [P2.sectionConstraints]: 'Izithintelo',
        [P2.sectionEdges]: 'Amaphetho',
        [P2.sectionFrame]: 'Isakhelo',
        [P2.sectionExposure]: 'Ukuvezwa',
        [P2.statusAdoptedFormal]: 'Yamkelwe (ngokusesikweni)',
        [P2.statusAdoptedInterpretive]: 'Yamkelwe (ngokutolika)',
        [P2.statusSuspended]: 'Inqunyanyisiwe',
        [P2.edgeEffectConstraint]: 'Yakha isithintelo (ingaphikisa)',
        [P2.edgeEffectResource]: 'Uvimba — akukho mpembelelo kwizigwebo',
        [P2.edgeEffectHeuristic]: 'I-heuristic — akukho mpembelelo kwizigwebo',
        [P2.edgeEffectShared]: 'Isiseko esabelwanayo — akukho mpembelelo kwizigwebo',
        [P2.frameActive]: '{count, plural, =0 {Akukho zithintelo zisebenzayo} one {# isithintelo esisebenzayo} other {# izithintelo ezisebenzayo}}',
        [P2.exposureNone]: 'Akukho kuxhomekeka kotoliko',
        [P2.exposureDep]: 'Ixhomekeke ku-{id}',
        [P2.laneTrackMachine]: 'Umatshini',
        [P2.laneTrackExpert]: 'Ingcali',
        [P2.laneTrackCommunity]: 'Uluntu',
        [P2.expertAudited]: 'Kuphononongiwe',
        [P2.expertInProgress]: 'Kuyaqhubeka',
        [P2.expertNotReviewed]: 'Akukahlolwa',
        [P2.communityFlags]: 'Kukho iimpawu',
        [P2.communityNone]: 'Akukho mpawu',
        [P2.controlChallenge]: 'Phikisa',
        [P2.controlActivate]: 'Yenza isebenze',
        [P2.controlMakeInterpretive]: 'Yenza ibe yeyokutolika',
        [P2.controlImportEdge]: 'Ngenisa iphetho',
        [P2.controlPromote]: 'Phakamisa',
        [P2.controlUndo]: 'Buyisela',
        [P2.controlRedo]: 'Yenza kwakhona',
        [P2.controlRegister]: 'Bhalisa',
        [P2.promoteRejected]: 'Ukunyuselwa kwaliwe',
        [P2.promoteAccepted]: 'Ukunyuselwa kwamkelwe',
        [P2.promoteReasonRefutationTerminal]: 'Ubungqina abunakuphilisa ukuphikiswa.',
        [P2.promoteReasonKeystone]: 'Ukunyuselwa okubalulekileyo kufuna inkxaso ye-empirical.',
        [P2.promoteReasonEmpiricalNotFixed]: 'Uhlobo lwe-empirical alulungiselelwanga.',
        [P2.promoteReasonModalNotFixed]: 'Inqanaba le-modal alilungiselelwanga.',
        [P2.promoteReasonWarrantNotImproved]: 'Inqanaba lesiqinisekiso aliphuculwanga.',
        [P2.evidenceComputation]: "{engine} computed {op} = {result} under {config}",
        [P2.lossNumericRounding]: "Result rounded to {dp} decimal places",
    },
};
//# sourceMappingURL=xh.js.map